/**
 * ShareButton — Copies a URL to clipboard with toast feedback.
 *
 * Uses navigator.clipboard with fallback for older browsers.
 * Stops event propagation to prevent parent click handlers (e.g., card navigation).
 *
 * Story 3.5 — Task 3
 */

import type { MouseEvent } from "react";

import { copyToClipboard } from "../../lib/clipboard";
import { cn } from "../../lib/cn";
import { useToast } from "../ui/Toast";

type ShareButtonProps = {
	url: string;
	className?: string;
};

export function ShareButton({ url, className }: ShareButtonProps) {
	const { addToast } = useToast();

	function handleClick(e: MouseEvent<HTMLButtonElement>) {
		e.stopPropagation();
		e.preventDefault();
		copyToClipboard(url)
			.then(() => {
				addToast({ type: "success", title: "Link copied!" });
			})
			.catch(() => {
				addToast({ type: "error", title: "Failed to copy link" });
			});
	}

	return (
		<button
			type="button"
			onClick={handleClick}
			className={cn(
				"inline-flex items-center justify-center h-8 w-8 rounded-full bg-white/80 text-pastel-gray hover:text-pastel-black hover:bg-white transition-colors shadow-sm",
				className
			)}
			aria-label="Copy link to clipboard"
		>
			<svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
				<path d="M12.232 4.232a2.5 2.5 0 013.536 3.536l-1.225 1.224a.75.75 0 001.061 1.06l1.224-1.224a4 4 0 00-5.656-5.656l-3 3a4 4 0 00.225 5.865.75.75 0 00.977-1.138 2.5 2.5 0 01-.142-3.667l3-3z" />
				<path d="M11.603 7.963a.75.75 0 00-.977 1.138 2.5 2.5 0 01.142 3.667l-3 3a2.5 2.5 0 01-3.536-3.536l1.225-1.224a.75.75 0 00-1.061-1.06l-1.224 1.224a4 4 0 005.656 5.656l3-3a4 4 0 00-.225-5.865z" />
			</svg>
		</button>
	);
}
